import { useRef, useEffect, useMemo, useLayoutEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { useTrackerTasksQuery, TaskType } from '../../../queries/useTrackerTasksQuery';
import { useConfigQuery } from '@buildwise/libs/configurations';
import useUserData from 'hooks/useUserData';
import { Badge, IconButton, Tooltip, Typography } from '@mui/material';
import { theme } from 'config/theme';
import CheckboxLineIcon from 'remixicon-react/CheckboxLineIcon';
import TrackerAPI from '@buildwise/libs/trackers/apis/TrackerAPI';
import { toast } from 'react-toastify';
import { useTrackerContext, useTrackerDetailsQuery } from '@buildwise/libs/trackers';
import StatusChip from '../Utils/StatusChip';
import TaskApplicableToIcon from '../Utils/TaskApplicableToIcon';
import TaskImpactIcon from '../Utils/TaskImpactIcon';
import { orderBy } from 'lodash';

function TrackerBoard() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { companyId } = useUserData();
  const {
    state: { trackerId, filteredTasks },
    dispatch,
  } = useTrackerContext();
  const { tracker } = useTrackerDetailsQuery({
    trackerId,
    shouldFetch: false,
  });
  const { activeUniqueEntries: taskStatus } = useConfigQuery({
    companyId,
    configName: tracker?.configNameForTasksStatus,
    shouldFetch: Boolean(tracker?.configNameForTasksStatus),
  });

  const { updateTasksCache } = useTrackerTasksQuery({
    trackerId,
    page: 0,
    size: 1000,
    shouldFetch: false,
  });

  // Split tasks by status
  const columns = useMemo(() => {
    const statusMap: Record<string, TaskType[]> = {};
    taskStatus.forEach((status) => {
      statusMap[status.entryCode] = [];
    });
    filteredTasks.forEach((task) => {
      if (statusMap[task.status]) {
        statusMap[task.status].push(task);
      }
    });
    Object.keys(statusMap).forEach((status) => {
      statusMap[status] = orderBy(statusMap[status], (value) => new Date(value.createdAt), ['asc']);
    });
    return statusMap;
  }, [filteredTasks, taskStatus]);

  // Helper to set height using ref before paint
  const setContainerHeightByRef = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const available = window.innerHeight - rect.top;
      const height = available > 100 ? available : 100;
      containerRef.current.style.height = `${height}px`;
      containerRef.current.style.maxHeight = `${height}px`;
    }
  };

  // Set height before first paint to avoid blink
  useLayoutEffect(() => {
    setContainerHeightByRef();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setContainerHeightByRef();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const onDragEnd = async (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    if (source.droppableId !== destination.droppableId) {
      // Moving task between columns
      const sourceTasks = Array.from(columns[source.droppableId]);
      const destTasks = Array.from(columns[destination.droppableId]);
      const [movedTask] = sourceTasks.splice(source.index, 1);
      const updatedTask: TaskType = {
        ...movedTask,
        status: destination.droppableId,
      };

      // Save previous state for rollback
      const prevTask = { ...movedTask };

      // Optimistically update cache for smoother UX
      updateTasksCache(updatedTask, 'update');

      try {
        // Save the updated status to the backend
        await TrackerAPI.editTask(companyId, movedTask.id, {
          ...movedTask,
          status: destination.droppableId,
        });
        // Optionally, you can refetch or update cache again if needed
      } catch (error) {
        // Revert cache to previous state if API fails
        updateTasksCache(prevTask, 'update');
        toast.error('Failed to update task status');
      }
    }
    // Do nothing for reordering within the same column
  };

  return (
    <Container ref={containerRef} sx={{ pt: 2 }}>
      <DragDropContext onDragEnd={onDragEnd}>
        {taskStatus.map((status) => (
          <Droppable droppableId={status.entryCode} key={status.entryCode}>
            {(provided) => (
              <Board {...provided.droppableProps} ref={provided.innerRef}>
                <BoardTitle>
                  <Typography
                    sx={(theme) => ({
                      color: theme.palette.custom.tuna,
                      fontWeight: 600,
                      fontSize: 14,
                    })}
                  >
                    <StatusChip
                      label={status.entryDescription || status.entryCode}
                      color={status.referenceObject?.color}
                    />
                  </Typography>
                  {columns[status.entryCode]?.length > 0 && (
                    <Badge
                      overlap="circular"
                      badgeContent={columns[status.entryCode]?.length || 0}
                      max={9999999999}
                      sx={(theme) => ({
                        '& .MuiBadge-badge': {
                          fontSize: 13,
                          fontWeight: 700,
                          color: theme.palette.custom.slate,
                          backgroundColor: theme.palette.custom.lightGray,
                          px: 1,
                          py: 1.25,
                        },
                      })}
                    />
                  )}
                </BoardTitle>
                <BoardContent>
                  {(columns[status.entryCode] || []).map((item, index) => (
                    <Draggable key={item.id} draggableId={item.id} index={index}>
                      {(provided, snapshot) => (
                        <TaskCard
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          dragging={snapshot.isDragging}
                          onClick={() => {
                            dispatch(({ state }) => ({
                              payload: {
                                ...state,
                                selectedTask: item,
                                // isOpenedTaskForm: true,
                              },
                            }));
                          }}
                        >
                          <Typography
                            sx={(theme) => ({
                              color: theme.palette.custom.tuna,
                              fontWeight: 600,
                              fontSize: 15,
                            })}
                          >
                            {item.title}
                          </Typography>
                          <Box
                            mt={1}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={1}
                          >
                            <Box>
                              {item.type && (
                                <Tooltip placement="bottom" title={<TooltipMsg>Type</TooltipMsg>}>
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <CheckboxLineIcon size={16} color={theme.palette.custom.link} />
                                    <Typography sx={{ fontSize: 13 }}>{item.type}</Typography>
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>

                            <Box display="flex" alignItems="center">
                              <Box>
                                {(item.applicableTo ||
                                  item.customFields?.['applicableTo']?.fieldValue) && (
                                  <Tooltip
                                    placement="bottom"
                                    title={
                                      <TooltipMsg>
                                        Applicable To:{' '}
                                        {item.applicableTo ||
                                          item.customFields?.['applicableTo']?.fieldValue}
                                      </TooltipMsg>
                                    }
                                  >
                                    <IconButton>
                                      <TaskApplicableToIcon
                                        applicableTo={
                                          item.applicableTo ||
                                          (item.customFields?.['applicableTo']
                                            ?.fieldValue as string)
                                        }
                                      />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                              <Box>
                                {(item.impact || item.customFields?.['impact']?.fieldValue) && (
                                  <Tooltip
                                    placement="bottom"
                                    title={
                                      <TooltipMsg>
                                        {item.impact || item.customFields?.['impact']?.fieldValue}{' '}
                                        impact
                                      </TooltipMsg>
                                    }
                                  >
                                    <IconButton size="small">
                                      <TaskImpactIcon
                                        impact={
                                          item.impact ||
                                          (item.customFields?.['impact']?.fieldValue as string)
                                        }
                                        size={22}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </TaskCard>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </BoardContent>
                {/* <Box>
                  <Button
                    fullWidth
                    variant="text"
                    startIcon={<AddLineIcon size={22} color={theme.palette.custom.mulledWine} />}
                    sx={(theme) => ({
                      textAlign: 'left',
                      justifyContent: 'flex-start',
                      borderRadius: 0,
                      fontWeight: 700,
                      fontSize: 14,
                      backgroundColor: theme.palette.custom.blueChalk,
                    })}
                    onClick={() => {
                      reset();
                      dispatch(({ state }) => ({
                        payload: {
                          ...state,
                          isOpenedTaskForm: true,
                          selectedTask: undefined,
                          taskFields: activeUniqueEntries,
                        },
                      }));
                    }}
                  >
                    Create
                  </Button>
                </Box> */}
              </Board>
            )}
          </Droppable>
        ))}
      </DragDropContext>
    </Container>
  );
}

const Container = styled(Box)(({ maxHeight }: { maxHeight?: number }) => ({
  display: 'flex',
  gap: 12,
  overflowX: 'auto',
  width: '100%',
  maxHeight: maxHeight,
  height: maxHeight,
  alignItems: 'stretch',
  paddingBottom: 8,
}));

const Board = styled(Box)(({ theme }) => ({
  background: theme.palette.custom.blueChalk,
  minWidth: 280,
  width: 250,
  borderRadius: 8,
  display: 'flex',
  flexDirection: 'column',
  height: 'fit-content',
  maxHeight: '100%',
  minHeight: 100,
  overflow: 'hidden',
}));

const BoardTitle = styled(Box)({
  zIndex: 1,
  padding: '12px 16px 8px',
  marginBottom: 8,
  display: 'flex',
  alignItems: 'center',
  gap: 20,
});

const BoardContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  minHeight: 0,
  padding: '0 8px',
  backgroundColor: theme.palette.custom.inputBackground,
}));

const TaskCard = styled(Box)<{ dragging: boolean }>(({ dragging }) => ({
  cursor: 'pointer !important',
  padding: '8px 12px',
  margin: '0 0 6px 0',
  borderRadius: 4,
  background: dragging ? theme.palette.custom.background : theme.palette.custom.white,
  boxShadow: '0 1px 1px #091e4240',
  '&:hover': {
    background: theme.palette.custom.background,
  },
}));

const TooltipMsg = styled(Typography)(({ theme }) => ({
  color: theme.palette.custom.white,
  fontSize: 12,
  fontWeight: 600,
}));

export default TrackerBoard;
